import {
  Button,
  InlineMessage,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@fountain/fountain-ui-components';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { ScoreCardEvaluatorsService } from 'api-clients/monolith/services/ScoreCardEvaluatorsService';
import React, { useEffect, useState, VFC } from 'react';
import { FormattedMessage } from 'react-intl';

import { StatusPill } from 'containers/ScorecardModals/InviteEvaluators/AssignedEvaluators';
import globalMessages from 'shared/global/messages';
import { trackAnalyticsEvent } from 'utils/analytics';

import { messages } from './messages';
import {
  ScorecardRequirement,
  ScorecardRequirementApplicant,
  ScorecardRequirementEvaluator,
} from './types';

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  // The table can outgrow the modal on narrow screens; it scrolls inside its
  // own container rather than widening the dialog.
  tableContainer: {
    overflowX: 'auto',
  },
  cappedNote: {
    color: theme.palette.text.secondary,
  },
  // The action label stays on one line in the compact bulk table.
  reminderCell: {
    whiteSpace: 'nowrap',
  },
}));

export interface ScorecardRequiredModalProps {
  requirement: ScorecardRequirement;
  // The signed-in user's external id: their own pending row offers Fill out
  // scorecard instead of a reminder they cannot send to themselves.
  currentUserId?: string | null;
  onClose: () => void;
  onFillOutScorecard: (
    applicant: ScorecardRequirementApplicant,
    evaluator: ScorecardRequirementEvaluator,
  ) => void;
}

const INTRO_MESSAGES = {
  all_evaluators: messages.introAllEvaluators,
  at_least_one: messages.introAtLeastOne,
  specific_number: messages.introSpecificNumber,
} as const;

const INTRO_MESSAGES_PLURAL = {
  all_evaluators: messages.introAllEvaluatorsPlural,
  at_least_one: messages.introAtLeastOnePlural,
  specific_number: messages.introSpecificNumberPlural,
} as const;

const rowKey = (
  applicant: ScorecardRequirementApplicant,
  evaluator: ScorecardRequirementEvaluator,
) => `${applicant.id}:${evaluator.template.id}:${evaluator.user.id ?? ''}`;

// The blocking modal shown when the backend refuses a move, reject, or bulk
// action because the from-stage's scorecard requirement is not met. Every
// number it shows comes from the server's refusal payload; nothing is
// recomputed here. Send reminder reuses the invite modal's endpoint and is
// offered only on rows the server marked remindable.
export const ScorecardRequiredModal: VFC<ScorecardRequiredModalProps> = ({
  requirement,
  currentUserId,
  onClose,
  onFillOutScorecard,
}) => {
  const classes = useStyles();

  const applicants = requirement.blocked_applicants;
  const isBulk = requirement.blocked_count > 1;
  const firstApplicant = applicants[0];
  const rows = applicants.flatMap(applicant => applicant.evaluators);
  // A pending evaluator nobody here can nudge or fill out still has a path:
  // the scorecard status label on the applicant, named in the hint below.
  const hasActionlessRow =
    rows.length === 0 ||
    rows.some(
      evaluator =>
        evaluator.status !== 'submitted' &&
        !evaluator.remindable &&
        evaluator.user.id !== currentUserId,
    );

  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [sentKeys, setSentKeys] = useState<string[]>([]);
  const [failedKey, setFailedKey] = useState<string | null>(null);

  useEffect(() => {
    trackAnalyticsEvent('scorecards:requirement_blocked_modal_view', {
      stage_id: requirement.stage.id,
      required_submissions: requirement.required_submissions,
      blocked_count: requirement.blocked_count,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendReminder = async (
    applicant: ScorecardRequirementApplicant,
    evaluator: ScorecardRequirementEvaluator,
  ) => {
    // One send at a time: the endpoint re-sends on every call by design, so
    // the guard against an impatient double click lives here.
    if (sendingKey || !evaluator.user.id) return;
    const key = rowKey(applicant, evaluator);
    setSendingKey(key);
    setFailedKey(null);
    try {
      await ScoreCardEvaluatorsService.postInternalApiScoreCardsTemplatesApplicantsEvaluatorsReminder(
        evaluator.template.id,
        applicant.id,
        evaluator.user.id,
      );
      setSentKeys(previous => [...previous, key]);
      trackAnalyticsEvent('scorecards:evaluator_reminder_send', {
        template_id: evaluator.template.id,
        evaluator_id: evaluator.user.id,
      });
    } catch {
      setFailedKey(key);
    } finally {
      setSendingKey(null);
    }
  };

  const intro = () => {
    const introMessages = isBulk ? INTRO_MESSAGES_PLURAL : INTRO_MESSAGES;
    return (
      <FormattedMessage
        {...introMessages[requirement.required_submissions]}
        values={{
          stageName: requirement.stage.name,
          applicantName: firstApplicant?.name,
          submittedCount: firstApplicant?.submitted_count,
          requiredCount:
            requirement.required_submissions === 'specific_number'
              ? requirement.required_submissions_count
              : firstApplicant?.required_count,
        }}
      />
    );
  };

  const reminderCell = (
    applicant: ScorecardRequirementApplicant,
    evaluator: ScorecardRequirementEvaluator,
  ) => {
    const key = rowKey(applicant, evaluator);
    if (sentKeys.includes(key)) {
      return (
        <Typography variant="body2" data-testid={`reminder-sent-${key}`}>
          <FormattedMessage {...messages.reminderSent} />
        </Typography>
      );
    }
    // The viewer's own row comes first: filling out their scorecard beats
    // emailing themselves a reminder, invited or not.
    if (
      evaluator.status !== 'submitted' &&
      evaluator.user.id != null &&
      evaluator.user.id === currentUserId
    ) {
      return (
        <Button
          autoWidth
          type="secondary"
          size="small"
          onClick={() => onFillOutScorecard(applicant, evaluator)}
          data-testid={`fill-out-scorecard-${key}`}
        >
          <FormattedMessage {...messages.fillOutScorecard} />
        </Button>
      );
    }
    if (evaluator.remindable && evaluator.user.id) {
      return (
        <Button
          autoWidth
          type="secondary"
          size="small"
          onClick={() => handleSendReminder(applicant, evaluator)}
          disabled={sendingKey !== null}
          isLoading={sendingKey === key}
          data-testid={`send-reminder-${key}`}
        >
          <FormattedMessage {...messages.sendReminder} />
        </Button>
      );
    }
    return null;
  };

  return (
    <Modal
      data-testid="scorecard-required-modal"
      ariaLabelledBy="scorecard-required-modal-header"
      open
      onClose={onClose}
      fullScreenOnMobile
      maxWidth={720}
    >
      <ModalHeader
        onClose={onClose}
        ariaLabelledBy="scorecard-required-modal-header"
        showIcon={false}
      >
        <FormattedMessage {...messages.title} />
      </ModalHeader>
      <ModalContent>
        <div className={classes.body}>
          <Typography variant="body2">{intro()}</Typography>
          {failedKey !== null && (
            <InlineMessage
              type="red"
              message={<FormattedMessage {...messages.reminderFailed} />}
            />
          )}
          {rows.length === 0 && (
            <Typography variant="body2" data-testid="no-evaluators-assigned">
              <FormattedMessage {...messages.noEvaluatorsAssigned} />
            </Typography>
          )}
          {rows.length > 0 && (
            <div className={classes.tableContainer}>
              <Table size="small" data-testid="scorecard-required-evaluators">
                <TableHead>
                  <TableRow>
                    {isBulk && (
                      <TableCell>
                        <FormattedMessage {...messages.applicantColumn} />
                      </TableCell>
                    )}
                    <TableCell>
                      <FormattedMessage {...messages.evaluatorColumn} />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage {...messages.scorecardColumn} />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage {...messages.statusColumn} />
                    </TableCell>
                    {/* Send reminder column — no label by design */}
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applicants.map(applicant =>
                    applicant.evaluators.map(evaluator => (
                      <TableRow key={rowKey(applicant, evaluator)}>
                        {isBulk && <TableCell>{applicant.name}</TableCell>}
                        <TableCell>{evaluator.user.name}</TableCell>
                        <TableCell>{evaluator.template.name}</TableCell>
                        <TableCell>
                          <StatusPill status={evaluator.status} />
                        </TableCell>
                        <TableCell
                          align="right"
                          className={classes.reminderCell}
                        >
                          {reminderCell(applicant, evaluator)}
                        </TableCell>
                      </TableRow>
                    )),
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          {hasActionlessRow && (
            <Typography
              variant="body2"
              className={classes.cappedNote}
              data-testid="invite-evaluators-hint"
            >
              <FormattedMessage {...messages.inviteEvaluatorsHint} />
            </Typography>
          )}
          {requirement.blocked_count > applicants.length && (
            <Typography variant="caption" className={classes.cappedNote}>
              <FormattedMessage
                {...messages.cappedApplicants}
                values={{
                  shownCount: applicants.length,
                  blockedCount: requirement.blocked_count,
                }}
              />
            </Typography>
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button
          autoWidth
          type="secondary"
          size="medium"
          onClick={onClose}
          data-testid="scorecard-required-close"
        >
          <FormattedMessage {...globalMessages.close} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};
